import React from "react";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import PropTypes from "prop-types";
import update from "immutability-helper";
import { get, map, flatten, size, filter, includes, indexOf } from "lodash";
import clsx from "clsx";

import { fetchGet, fetchPost } from "../../api/fetch";

import Grid from "@material-ui/core/Grid";
import Checkbox from "@material-ui/core/Checkbox";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";

import CustomButton from "../Common/CustomButton";
import Picture from "../Common/Picture";
import Loader from "../Common/Loader";
import CloseIcon from "../Common/CloseIcon";
import UploadPhotos from "./UploadPhotos";

import {
  STATUS_INDEXED,
  STATUS_UNINDEXED,
  STATUS_ARCHIVED,
  STATUS_FAILED,
  STATUS_CAPTURED,
  ACTION_INDEX,
  ACTION_UNINDEX,
  ACTION_ARCHIVE,
  ACTION_COPY_TO_INDEXED,
  ACTION_COPY_TO_UNINDEXED,
} from "../../constants/profiles";
import { API_LUKE } from "../../constants/api";
import { isUnknownProfile } from "../../utils/profile";

import colors from "../../assets/style/colors";
import { isAllowed } from "../../utils/users";

const styles = {
  container: {
    height: "calc(100vh - 175px)",
    padding: "0px 12px 12px",
  },
  card: {
    height: "calc(100vh - 200px)",
    maxWidth: "calc(100vw - 113px)",
    overflow: "auto",
    padding: "12px",
    margin: "12px",
    backgroundColor: colors.white,
    boxShadow: "0px 2px 2px 0px " + colors.black23,
    borderRadius: "2px",
  },
  imageCard: {
    border: `1px solid ${colors.black542}`,
    padding: "12px 0px",
    margin: "12px",
    borderRadius: "2px",
    maxWidth: "431px",
    overflow: "auto",
  },
  actionButton: {
    width: "24px",
    height: "20px",
    marginLeft: "auto",
    cursor: "pointer",
  },
  dot: {
    backgroundColor: colors.primary,
    borderRadius: "50%",
    width: "4px",
    height: "4px",
    marginRight: "2px",
  },
  disabledDot: {
    backgroundColor: colors.darkGrey,
    borderRadius: "50%",
    width: "4px",
    height: "4px",
    marginRight: "2px",
  },
  menuItem: {
    fontSize: "1em",
    paddingLeft: "16px",
    color: colors.black54,
    height: "40px",
    "&:hover": {
      backgroundColor: colors.lightGreenBg,
    },
  },
  checkbox: {
    padding: "0px",
  },
  link: {
    color: colors.primary,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  closeIcon: {
    display: "flex",
    justifyContent: "flex-end",
  },
  rootDialogWarning: {
    width: "350px",
  },
};

const ProfileImages = (props) => {
  const {
    classes,
    profileType,
    profileId,
    refreshProfile,
    isMe,
    permissions,
  } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openedMenu, setOpenedMenu] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [photos, setPhotos] = React.useState({
    [STATUS_INDEXED]: [],
    [STATUS_UNINDEXED]: [],
    [STATUS_FAILED]: [],
    [STATUS_CAPTURED]: [],
    [STATUS_ARCHIVED]: [],
  });
  const [checkedPhotos, setCheckedPhotos] = React.useState({
    [STATUS_INDEXED]: [],
    [STATUS_UNINDEXED]: [],
    [STATUS_CAPTURED]: [],
  });

  const [showArchived, setShowArchived] = React.useState(false);
  const [renderIndexWarning, setRenderIndexWarning] = React.useState(false);

  React.useEffect(() => {
    fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchImages = async () => {
    setLoading("all");
    try {
      let profileImages = await fetchGet(`profiles/${profileId}/images`);
      let indexed = [];
      let unindexed = [];
      let archived = [];
      let failed = [];

      profileImages.forEach((img) => {
        if (!img.active) {
          archived.push(img);
        } else if (profileType !== 1) {
          unindexed.push(img);
        } else {
          if (img.active) {
            if (img.enrollment_error) {
              failed.push(img);
            } else if (!img.enrollment_error) {
              if (img.enrollment_status) {
                indexed.push(img);
              } else if (!img.enrollment_status) {
                unindexed.push(img);
              }
            }
          }
        }
      });
      fetchCapturedImages({
        [STATUS_INDEXED]: indexed,
        [STATUS_UNINDEXED]: unindexed,
        [STATUS_FAILED]: failed,
        [STATUS_ARCHIVED]: archived,
      });
    } catch (e) {
      console.log(e);
    }
  };

  const fetchCapturedImages = async (existingPhotos, offset = 0) => {
    if (offset) {
      setLoading("loadMore");
    }
    try {
      let eventGroups = await fetchPost("new/event_groups", {
        pagination: {
          offset,
          limit: 3,
        },
        filter: {
          profile_id: profileId,
          types: [1, 10, 11, 12],
        },
        data: [
          "id",
          "active",
          "type",
          "profile",
          {
            name: "events",
            data: ["id", "active", "profile", "images"],
          },
        ],
      });
      eventGroups = get(eventGroups, ["items"]);

      let captured = flatten(
        map(eventGroups, (eg) =>
          flatten(map(get(eg, ["events"]), (e) => flatten(get(e, ["images"]))))
        )
      );

      if (existingPhotos) {
        setPhotos({
          ...existingPhotos,
          [STATUS_CAPTURED]: captured,
        });
      } else {
        setPhotos(
          update(photos, {
            [STATUS_CAPTURED]: offset
              ? { $push: captured }
              : { $set: captured },
          })
        );
      }
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  };

  const checkPhoto = (cat, id) => {
    if (get(checkedPhotos, [cat])) {
      let index = indexOf(get(checkedPhotos, [cat]), id);
      if (index === -1) {
        // Add to checked photos
        setCheckedPhotos(
          update(checkedPhotos, {
            [cat]: { $push: [id] },
          })
        );
      } else {
        // Remove from checked photos
        setCheckedPhotos(
          update(checkedPhotos, {
            [cat]: { $splice: [[index, 1]] },
          })
        );
      }
    }
  };

  const executeAction = async (action, cat) => {
    try {
      setLoading(cat);
      if (
        includes([ACTION_COPY_TO_INDEXED, ACTION_COPY_TO_UNINDEXED], action)
      ) {
        let newImagesIds = [];
        for (let i = 0; i < size(get(checkedPhotos, [cat])); i++) {
          // Download image
          let imgName = get(
            filter(
              get(photos, [STATUS_CAPTURED]),
              (p) => p.id === get(checkedPhotos, [cat, i])
            ),
            [0, "url"]
          );
          if (includes(imgName, "/api/images/")) {
            let index = imgName.indexOf("/api/images/");
            imgName = imgName.substring(index + 12);
          }
          let img = await fetchGet(`images/${imgName}`);
          let body = {
            name: imgName,
            enrollment_status: false,
            enrollment_error: false,
            main_display: false,
            "image-base64": img,
          };
          // Add image to the profile
          let newImg = await fetchPost(`profiles/${profileId}/images`, body);
          newImagesIds.push(get(newImg, ["id"]));
        }
        if (action === ACTION_COPY_TO_INDEXED) {
          // Enroll the new images
          await fetchPost(
            `profiles/${profileId}/index`,
            {
              ImageIds: newImagesIds,
            },
            API_LUKE
          );
        }
      } else {
        let apiAction;
        if (action === ACTION_ARCHIVE) {
          apiAction = "archive";
        } else if (action === ACTION_INDEX) {
          apiAction = "index";
        } else if (action === ACTION_UNINDEX) {
          apiAction = "unindex";
        }
        if (size(get(checkedPhotos, [cat])) > 0) {
          await fetchPost(
            `profiles/${profileId}/${apiAction}`,
            {
              ImageIds: checkedPhotos[cat],
            },
            API_LUKE
          );
        }
      }
      // Refresh images
      //setTimeout(async () => {
      await fetchImages();
      setCheckedPhotos(update(checkedPhotos, { [cat]: { $set: [] } }));
      setLoading(false);
      refreshProfile();

      if (includes([ACTION_INDEX, ACTION_COPY_TO_INDEXED], action)) {
        setRenderIndexWarning(true);
      }
      //}, size(get(checkedPhotos, [cat])) * 5000);
    } catch (e) {
      console.log(e);
    }
  };

  const selectAll = (title) => {
    if (checkedPhotos[title].length === photos[title].length) {
      setCheckedPhotos(
        update(checkedPhotos, {
          [title]: { $set: [] },
        })
      );
    } else {
      setCheckedPhotos(
        update(checkedPhotos, {
          [title]: { $set: photos[title].map((p) => p.id) },
        })
      );
    }
  };

  const renderActionMenu = (title) => {
    if (includes([STATUS_INDEXED, STATUS_UNINDEXED, STATUS_CAPTURED], title)) {
      return (
        <>
          <Grid
            container
            justify="flex-end"
            alignItems="center"
            className={classes.actionButton}
            style={{
              cursor:
                size(get(checkedPhotos, [title])) > 0 ? "pointer" : "default",
            }}
            onClick={(event) => {
              if (size(get(checkedPhotos, [title])) > 0) {
                setAnchorEl(event.currentTarget);
                setOpenedMenu(title);
              }
            }}
          >
            {map(new Array(3), (item, index) => (
              <Grid
                item
                key={index}
                className={
                  size(get(checkedPhotos, [title])) > 0
                    ? classes.dot
                    : classes.disabledDot
                }
              />
            ))}
          </Grid>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            getContentAnchorEl={null}
          >
            {!includes([STATUS_CAPTURED], openedMenu) && (
              <MenuItem
                classes={{ root: classes.menuItem }}
                alignItems="center"
                onClick={() => {
                  setAnchorEl(null);
                  executeAction(ACTION_ARCHIVE, openedMenu);
                }}
              >
                {I18n.get("profiles_archivedSelectedPhotos")}
              </MenuItem>
            )}
            {profileType === 1 && includes([STATUS_UNINDEXED], openedMenu) && (
              <MenuItem
                classes={{ root: classes.menuItem }}
                alignItems="center"
                onClick={() => {
                  setAnchorEl(null);
                  executeAction(ACTION_INDEX, openedMenu);
                }}
              >
                {I18n.get("profiles_enrollSelectedPhotos")}
              </MenuItem>
            )}
            {profileType === 1 && includes([STATUS_INDEXED], openedMenu) && (
              <MenuItem
                classes={{ root: classes.menuItem }}
                alignItems="center"
                onClick={() => {
                  setAnchorEl(null);
                  executeAction(ACTION_UNINDEX, openedMenu);
                }}
              >
                {I18n.get("profiles_unindexSelectedPhotos")}
              </MenuItem>
            )}
            {profileType === 1 && includes([STATUS_CAPTURED], openedMenu) && (
              <MenuItem
                key={ACTION_COPY_TO_INDEXED}
                classes={{ root: classes.menuItem }}
                alignItems="center"
                onClick={() => {
                  setAnchorEl(null);
                  executeAction(ACTION_COPY_TO_INDEXED, openedMenu);
                }}
              >
                {I18n.get("profiles_enrollSelectedPhotos")}
              </MenuItem>
            )}
            {includes([STATUS_CAPTURED], openedMenu) && (
              <MenuItem
                key={ACTION_COPY_TO_UNINDEXED}
                classes={{ root: classes.menuItem }}
                alignItems="center"
                onClick={() => {
                  setAnchorEl(null);
                  executeAction(ACTION_COPY_TO_UNINDEXED, openedMenu);
                }}
              >
                {I18n.get(
                  profileType === 1
                    ? "profiles_copyToUnindexed"
                    : "profiles_copyToContext"
                )}
              </MenuItem>
            )}
          </Menu>
        </>
      );
    }
    return null;
  };

  const renderImageCard = (title, images) => {
    if (title === STATUS_ARCHIVED && !showArchived) {
      return null;
    }
    return (
      <Grid item xs className={classes.imageCard}>
        <Grid container direction="column" wrap="nowrap">
          <Grid item style={{ padding: "0px 12px" }}>
            <Grid container wrap="nowrap">
              <Grid
                item
                xs
                className="text-87"
                style={{
                  textTransform: "uppercase",
                  marginBottom: "12px",
                }}
              >
                <span style={{ marginRight: "10px" }}>
                  {profileType !== 1 && title === STATUS_UNINDEXED
                    ? I18n.get("profiles_context")
                    : I18n.get(
                        `profiles_${title
                          .substring(7, title.length)
                          .toLowerCase()}`
                      )}
                </span>
                {loading === title && <CircularProgress size={14} />}
              </Grid>
              <Grid item xs={3}>
                {isMe && !isAllowed(permissions, "me.photos.update")
                  ? null
                  : renderActionMenu(title)}
              </Grid>
            </Grid>
          </Grid>
          <Grid item style={{ height: "20px", paddingLeft: "12px" }}>
            {includes(
              [STATUS_INDEXED, STATUS_UNINDEXED, STATUS_CAPTURED],
              title
            ) &&
              size(get(photos, [title])) > 0 && (
                <span className={classes.link} onClick={() => selectAll(title)}>
                  {size(get(checkedPhotos, [title])) ===
                  size(get(photos, [title]))
                    ? I18n.get("profiles_deselectAll")
                    : I18n.get("profiles_selectAll")}
                </span>
              )}
          </Grid>
          <Grid item>
            <Grid
              container
              style={{
                maxHeight: "calc(100vh - 440px)",
                overflow: "auto",
                paddingLeft: "12px",
              }}
            >
              {map(images, (img) => (
                <Grid item key={img.id}>
                  <Grid container direction="column">
                    <Grid
                      item
                      style={{
                        width: "72px",
                        height: "72px",
                        margin: "5px 8px 5px 0px",
                        border: `1px solid ${colors.veryLighPink}`,
                      }}
                    >
                      <Picture
                        maxHeight={70}
                        style={{ maxHeight: "70px", maxWidth: "70px" }}
                        url={img.url}
                        alt="profile_picture"
                      />
                    </Grid>
                    {includes(
                      [STATUS_INDEXED, STATUS_UNINDEXED, STATUS_CAPTURED],
                      title
                    ) && (
                      <Grid
                        item
                        style={{ textAlign: "right", marginRight: "5px" }}
                      >
                        <Checkbox
                          checked={includes(
                            get(checkedPhotos, [title]),
                            img.id
                          )}
                          onChange={() => checkPhoto(title, img.id)}
                          color="primary"
                          classes={{ root: classes.checkbox }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
          {title === STATUS_CAPTURED && (
            <Grid item style={{ textAlign: "center", marginTop: "20px" }}>
              {loading === "loadMore" ? (
                <CircularProgress size={14} />
              ) : (
                <span
                  className={classes.link}
                  onClick={() =>
                    fetchCapturedImages(
                      null,
                      size(get(photos, [STATUS_CAPTURED]))
                    )
                  }
                >
                  {I18n.get("profiles_loadMore")}
                </span>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderIndexWarningDialog = () => {
    return (
      <Dialog
        open={renderIndexWarning}
        classes={{ paper: classes.rootDialogWarning }}
      >
        <Grid container direction="column" style={{ padding: "6px" }}>
          <Grid item>
            <Grid container alignItems="center">
              <Grid item xs={2} />
              <Grid
                item
                xs
                className="text-87 bold-500 text-center flexCenter"
                style={{ justifyContent: "center", textTransform: "uppercase" }}
              >
                {I18n.get("profiles_indexingPhotosInProgress")}
              </Grid>
              <Grid item xs={2} className={classes.closeIcon}>
                <CloseIcon closeFunction={() => setRenderIndexWarning(false)} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item className="text-54" style={{ padding: "18px" }}>
            {I18n.get("profiles_indexWarning")}
          </Grid>
          <Grid item style={{ margin: "20px 0px", textAlign: "center" }}>
            <CustomButton
              color="primary"
              variant="contained"
              onClick={() => setRenderIndexWarning(false)}
            >
              {I18n.get("profiles_pass")}
            </CustomButton>
          </Grid>
        </Grid>
      </Dialog>
    );
  };

  return (
    <Grid
      container
      direction="column"
      className={clsx(classes.container, classes.card)}
      wrap="nowrap"
    >
      {renderIndexWarningDialog()}
      <Grid item container style={{ padding: "12px 0px" }}>
        <Grid item style={{ paddingLeft: "12px" }}>
          {!isUnknownProfile(profileId) && (
            <UploadPhotos
              profileId={profileId}
              onUpload={() => {
                fetchImages();
                refreshProfile();
              }}
              disabled={isMe && !isAllowed(permissions, "me.photos.create")}
            />
          )}
        </Grid>
        <Grid item xs />
        {loading !== "all" && (
          <Grid item style={{ paddingRight: "12px" }}>
            <span
              className={classes.link}
              onClick={() => setShowArchived(!showArchived)}
            >
              {I18n.get(
                showArchived ? "profiles_hideArchived" : "profiles_showArchived"
              )}
            </span>
          </Grid>
        )}
      </Grid>
      <Grid item className="height-100">
        <Grid container wrap="nowrap" style={{ height: "calc(100% - 20px)" }}>
          {loading === "all" ? (
            <Grid item style={{ width: "100%", display: "flex" }}>
              <Loader noDecoration />
            </Grid>
          ) : (
            map(
              profileType === 1
                ? [
                    STATUS_INDEXED,
                    STATUS_UNINDEXED,
                    STATUS_FAILED,
                    STATUS_CAPTURED,
                    STATUS_ARCHIVED,
                  ]
                : [STATUS_UNINDEXED, STATUS_CAPTURED, STATUS_ARCHIVED],
              (type) => (
                <React.Fragment key={type}>
                  {renderImageCard(type, photos[type])}
                </React.Fragment>
              )
            )
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

ProfileImages.propTypes = {
  profileId: PropTypes.string.isRequired,
  refreshProfile: PropTypes.func.isRequired,
};

export default withStyles(styles)(ProfileImages);
